import { useState, useEffect, useCallback, useContext } from 'react';
import Table from 'react-bootstrap/Table';
import {CourseEvent} from '../../models/CourseEvent';
// import {availableCourses} from '../../dummydata/availableCourses';
// import {upcomingCourses} from '../../dummydata/upcomingCourses'
import Button from 'react-bootstrap/Button';
import { DataTableColumn } from '../widget/DataTable';
import { PencilSquare, ChevronDown, ChevronUp, Trash, CaretDownFill, CaretUpFill } from 'react-bootstrap-icons';
import { CourseEventModal } from './modal/CourseEventModal';
import { CourseEventExpandable } from './CourseEventExpandable';
import { DeleteEventModal } from './modal/DeleteEventModal';
import PaginationGroup from '../widget/Pagination';
import { getData } from '../../utils/GetData';
import { sharedSearchCache } from '../../../api/SearchCache';
import { SessionContext } from '../../context/SessionContext';
import { displayDateTimeAsVtTime } from '../../utils/DateManagement';

export const CourseEventList = () => {
    const [courses, setCourses] = useState<CourseEvent[] | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: "asc" | "desc" | "none" }>({ column: null, direction: "asc" });
    const [pageSize, setPageSize] = useState(5);
    const [searchError, setSearchError] = useState<string | null>(null);

    const [selectedCourse, setSelectedCourse] = useState<CourseEvent | undefined>(undefined);
    const [showModal, setShowModal] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [needsReload, setNeedsReload] = useState(true);

    const [showDelete, setShowDelete] = useState(false);

    const sessionInfo = useContext(SessionContext);
    const sessionUser = sessionInfo?.user ? sessionInfo.user : null;

    const cols: DataTableColumn<CourseEvent>[] = [
        {column: "arrow", label:""},
        {column: "title", label: "Title", headerClassName: "w-auto", sortable: true},
        {column: "location", label: "Location", headerClassName: "w-auto", sortable: true},
        {column: "location_details", label: "Location Details", headerClassName: "w-auto", sortable: true },
        {column: "start_date", label: "Start Date", headerClassName: "w-auto", sortable: true,
            accessor: (row: any) => (
                <>
                    <div>{displayDateTimeAsVtTime(row.start_date)}</div>
                </>
            )
        },
        {column: "end_date", label: "End Date", headerClassName: "w-auto", sortable: true,
            accessor: (row: any) => (
                <>
                    <div>{displayDateTimeAsVtTime(row.end_date)}</div>
                </>
            )
        },
        {column: "hours", label: "Hours", headerClassName: "w-auto", sortable: true},
        {column: "open_date", label: "Open Date", headerClassName: "w-auto", sortable: true,
            accessor: (row: any) => (
                <>
                    <div>{displayDateTimeAsVtTime(row.open_date)}</div>
                </>
            )
        },
        {column: "close_date", label: "Close Date", headerClassName: "w-auto", sortable: true,
            accessor: (row: any) => (
                <>
                    <div>{displayDateTimeAsVtTime(row.close_date)}</div>
                </>
            )
        },
        {column: "max_registrations", label: "Max Reg.", headerClassName: "w-auto", sortable: true},
        {column: "current_registrations", label: "Current Reg.", headerClassName: "w-auto", sortable: true},
        {column: "waitlist", label: "Waitlist", headerClassName: "w-auto", sortable: true},
        
        
    ];
    
    useEffect(() => {
        if(sessionUser && sessionUser.user_role === 'Admin'){
            cols.push(
                {column: "edit", label: "Edit", headerClassName: "w-auto", cellClassName:"align-middle",
                    accessor: () => (
                        <Button className="text-light" aria-label="Edit"><PencilSquare/></Button>
                    )
                },
                {column: "delete", label: "Delete", headerClassName: "w-auto", cellClassName:"align-middle",
                    accessor: () => (
                        <Button className="text-light" aria-label="Delete"><PencilSquare/></Button>
                    )
                }
            );
        }
    }, []);

    const handleDeleteClose = () => {
        setShowDelete(false);
        setSelectedCourse(undefined);
    };

    const getCourses = async () => {
        const result = await getData<CourseEvent>(
            '/api/course-event/search', 
            page, 
            pageSize,
            {
                column: sort.column,
                direction: sort.direction
            }
        );

        if(typeof result === 'object') {
            const courseEventsResult: CourseEvent[] = result.data.map((row: any) => ({
                course_event_id: Number(row.course_event_id),
                course_id: Number(row.course_id),
                title: row.title,
                hours: row.hours,
                location: row.location,
                close_date: new Date(Date.parse(row.close_date)),
                start_date: new Date(Date.parse(row.start_date)),
                end_date: new Date(Date.parse(row.end_date)),
                open_date: new Date(Date.parse(row.open_date)),
                max_registrations: row.max_registrations,
                current_registrations: row.current_registrations,
                description: row.description,
                location_details: row.location_details,
                waitlist: row.waitlist
            }));
            setCourses(courseEventsResult);
            setTotal(result.total);
            setSearchError(null);
        } else {
            setSearchError(result);
        }
    };

    useEffect(() => {
        setNeedsReload(true);
    }, [page, sort, pageSize]);

    useEffect(() => {
        if(needsReload) {
            getCourses();
            setNeedsReload(false);
        }
    }, [needsReload]);

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedCourse(undefined);
    };

    const handleChangesInChildren = () => {
        setNeedsReload(true);
    };

    const initiateReload = useCallback(() => {
        sharedSearchCache.clear();
        setTimeout(() => {
            setNeedsReload(true);
        }, 1);
    }, [setNeedsReload]);

    const ExpandableRow: React.FC<{rowData: CourseEvent, i: number}> = ({rowData, i}) => {
        const [open, setOpen] = useState(false);

        return (
            <>
                <tr onClick={() => setOpen(!open)} aria-controls={`collapse-${i}`} aria-expanded={open} style={{ cursor: 'pointer' }}>
                    <td>
                        
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(!open);
                            }}
                            aria-controls={`collapse-${i}`}
                            aria-expanded={open}
                            size="sm"
                            variant="light"
                            aria-label="Expand Row"
                        >
                            {open ? 
                                <ChevronUp></ChevronUp>
                                :
                                <ChevronDown></ChevronDown> 
                            }
                        </Button>
                    </td>
                    <td>{rowData.title}</td>
                    <td>{rowData.location}</td>
                    <td>{rowData.location_details}</td>
                    <td>{displayDateTimeAsVtTime(rowData.start_date)}</td>
                    <td>{displayDateTimeAsVtTime(rowData.end_date)}</td>
                    <td>{rowData.hours}</td>
                    <td>{displayDateTimeAsVtTime(rowData.open_date)}</td>
                    <td>{displayDateTimeAsVtTime(rowData.close_date)}</td>
                    <td>{rowData.max_registrations}</td>
                    <td>{rowData.current_registrations}</td>
                    <td>{rowData.waitlist}</td>
                    {sessionUser && sessionUser.user_role === "Admin" &&
                        <>
                            <td>
                                <Button 
                                    className="text-light"
                                    onClick={() => {
                                        setIsNew(false);
                                        setSelectedCourse(rowData); 
                                        setShowModal(true);
                                    }}
                                    aria-label="Edit"
                                >
                                    <PencilSquare/>
                                </Button>
                            </td>
                            <td>
                                <Button 
                                    variant="danger" 
                                    className="text-light" 
                                    onClick={() => {
                                        setSelectedCourse(rowData);
                                        setShowDelete(true);
                                    }}
                                    aria-label="Delete"
                                >
                                    <Trash/>
                                </Button>
                            </td>
                        </>
                    }
                    
                </tr>
                
                <CourseEventExpandable rowData={rowData} i={i} open={open} initiateParentReload={handleChangesInChildren}/>
            </>
        )
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);  // Reset to first page when changing items per page
    };

    const handleSort = (col: DataTableColumn<CourseEvent>) => {
        if (!col.sortable) return;
        let direction: "asc" | "desc" | "none" = "asc";
        if (sort.column === col.column) {
            if (sort.direction === "asc") {
                direction = "desc";
            } else if (sort.direction === "desc") {
                direction = "none";
            } else {
                direction = "asc";
            }
        }
        const newSort = { column: direction === "none" ? null : col.column, direction };
        setSort(newSort);
        setPage(1);
        initiateReload();
    };

    return (
        
        <div>
            {selectedCourse !== undefined &&
                <CourseEventModal show={showModal} onHide={handleModalClose} courseEvent={selectedCourse} isNew={isNew} initiateReload={initiateReload}/>
            }

            {selectedCourse !== undefined &&
                <DeleteEventModal courseEvent={selectedCourse} onHide={handleDeleteClose} show={showDelete} 
                    initiateReload={() => {
                        // if there is only one course on the current page and it was deleted, set the page to the prev page
                        if(courses?.length == 1 && page > 1) setPage(page - 1);
                        initiateReload();
                    }}
                />
            }
            <h1 className="text-light">Course Event List</h1>
            <h2 className="text-light" style={{fontSize: '1rem'}}>For searching for and building course events.</h2>
            { sessionUser && sessionUser.user_role === 'Admin' &&
                <Button 
                    variant="success" 
                    style={{boxShadow:'0 10px 12px 0 rgba(0, 0, 0, 0.25)'}}
                    onClick={() => {
                        setIsNew(true);
                        // set to a blank course
                        setSelectedCourse({
                            course_event_id: 0,
                            course_id: 0,
                            title: '',
                            hours: 0,
                            location: '',
                            close_date: new Date(),
                            start_date: new Date(),
                            end_date: new Date(),
                            open_date: new Date(),
                            max_registrations: 30,
                            current_registrations: 0,
                            description: '',
                            location_details: '',
                            waitlist: 0
                        });
                        setShowModal(true);
                    }}
                >
                    Add Course Event
                </Button>
            }
            
            <hr className="border"/>

            <div style={{padding:"1em", backgroundColor:"white", borderRadius:"0.5em"}}>
                {searchError &&
                    <div>
                        <strong>Search error:</strong> {searchError}
                    </div>
                }
                {courses &&
                    <>
                    <Table responsive>
                        <thead>
                            <tr>
                                {cols.map((col:any) => {
                                    if(col.column === 'arrow') {
                                        return (
                                            <td key={col.column}></td>
                                        )
                                    }
                                    return(
                                        <th key={col.column} onClick={() => handleSort(col)} className={col.sortable ? 'sortable-header' : ''}>
                                            <div className="d-flex gap-2 align-items-center">
                                                <div>{col.label}</div>
                                                {sort.column === col.column && sort.direction === 'asc' &&
                                                    <CaretUpFill className="text-secondary"/>
                                                }
                                                {sort.column === col.column && sort.direction === 'desc' &&
                                                    <CaretDownFill className="text-secondary"/>
                                                }
                                            </div>
                                            
                                        </th>
                                    )
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {courses && courses.map((course, i) => (
                                <ExpandableRow rowData={course} i={i} key={`course_${i}`}/>
                            ))}

                        </tbody>
                    </Table>
                    <PaginationGroup 
                        page={page} 
                        totalPages={Math.ceil(total / pageSize)}
                        onPageChange={setPage} 
                        itemsPerPage={pageSize}
                        onItemsPerPageChange={handlePageSizeChange}               
                    />
                    </>
                }   
            </div>
        </div>
    );
}